import React, { useEffect, useRef, useState } from 'react';

/** About fifteen lines of the list's text. */
export const LIST_PREVIEW_HEIGHT = '22.5em';

// Black where the content shows, fading to clear over the last line and a half. A mask rather than a gradient
// overlay, so it needs no background colour and is right in light and dark mode alike.
const FADE = 'linear-gradient(to bottom, black calc(100% - 2.25em), transparent)';

/**
 * A response in the Service Catalog list, cut at a fixed height, faded and followed by "..." when there is more. The
 * height used to be estimated from the Markdown - so many lines per line of text, four per picture - which was wrong
 * whenever a picture was small: a thin rule or an arrow icon spent four lines each. The browser knows the real
 * height, so it decides. The fade and the "..." only appear when something is actually cut, which is measured again
 * whenever the content changes size - pictures arrive after the text.
 */
export const ClampedPreview = ({ children, more = false }: { children: React.ReactNode; more?: boolean }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () => setCut(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    Array.from(element.children).forEach(child => observer.observe(child));
    // A picture loading changes the content's height without resizing the box itself.
    element.addEventListener('load', measure, true);
    return () => {
      observer.disconnect();
      element.removeEventListener('load', measure, true);
    };
  }, [children]);

  // The fade alone did not say whether there was more: a cut that fell between lines, or under a picture, looked like
  // the end. The "..." below the box is what says so - for this height cap, and for the text cut (`more`) alike, so
  // a cut-off response always ends the same way.
  return (
    <>
      <div
        ref={ref}
        style={{ maxHeight: LIST_PREVIEW_HEIGHT, overflow: 'hidden', ...(cut ? { maskImage: FADE, WebkitMaskImage: FADE } : {}) }}
      >
        {children}
      </div>
      {(cut || more) && <div aria-hidden="true">...</div>}
    </>
  );
};
